<template>
    <div class="d-flex align-center hotel-map-location-filter">
        <v-autocomplete
            ref="input"
            v-model="location"
            :items="items"
            :placeholder="$t('search_on_map')"
            bg-color="white"
            density="compact"
            hide-no-data
            hide-selected
            hide-details
            no-filter
            clearable
            return-object
            single-line
            variant="outlined"
            class="me-3 location"
            menu-icon=""
            item-title="place_id"
            item-value="place_id"
            @update:model-value="change"
            @click:clear="clear"
            @update:search="onSearch"
        >
            <template v-slot:append-inner>
                <v-icon v-if="!loading">mdi-magnify</v-icon>
                <v-progress-circular v-else indeterminate size="24" color="grey" />
            </template>
            <template v-slot:item="{item, props}">
                <v-list-item v-bind="props">
                    <template v-slot:title>
                        <span v-text="item.raw.formatted_address" />
                    </template>
                </v-list-item>
            </template>
            <template v-slot:selection="{item}">
                {{ item.raw.formatted_address }}
            </template>
        </v-autocomplete>
        <v-text-field
            v-show="!hideRadius && circle"
            v-model="radius"
            :disabled="!circle"
            :min="0"
            :max="1000"
            :suffix="$t('km')"
            bg-color="white"
            type="number"
            density="compact"
            hide-details
            single-line
            variant="outlined"
            class="me-2 radius-field input--no-arrows"
            @update:model-value="setRadius"
        />
        <v-btn v-if="!hideRadius" color="white" class="reset-btn" @click="!circle ? setCircle(cityLocation) : clear()">
            <v-icon>{{ !circle ? `mdi-circle-double` : 'mdi-close-circle' }}</v-icon>
        </v-btn>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, mixins} from 'vue-facing-decorator'
    import {clone} from '@/utils/helpers'
    import {EventBus, HIDE_CIRCLE_ON_MAP, SEARCH_EVENT} from '@/utils/event-bus'
    import {HotelMapMixin} from '@/components/hotels/mixins/HotelMapMixin.vue'
    import {distanceTransformer} from '@/utils/hotels/hotel-transformers'

    @Component
    export class HotelMapLocationFilter extends mixins(HotelMapMixin) {
        @Prop({required: true}) google
        @Prop({required: true}) map
        @Prop({required: true}) city
        @Prop({default: false, type: Boolean}) hideRadius

        timer = null
        items = []
        loading = false
        location = null
        circle = null
        radius = null
        marker = null

        mounted() {
            EventBus.$on(SEARCH_EVENT, this.clear)
            EventBus.$on(HIDE_CIRCLE_ON_MAP, this.clear)
            this.radius = (Number(hotelsRuntimeStore.runtimeFilters.map.radius) / 1000).toFixed(2)
        }

        beforeUnmount() {
            EventBus.$off(SEARCH_EVENT, this.clear)
            EventBus.$off(HIDE_CIRCLE_ON_MAP, this.clear)
        }

        onSearch(val) {
            if (this.timer) {
                clearTimeout(this.timer)
            }
            this.timer = setTimeout(() => {
                this.initSearch(val)
            }, 500)
        }

        async initSearch(val) {
            if (this.loading || (this.items.length > 0 && this.items.indexOf(val) !== -1) || !val) return
            this.loading = true
            const location = this.cityLocation
            /*await this.$axios.$get('https://maps.googleapis.com/maps/api/place/findplacefromtext/json', {
                params: {
                    input: val,
                    locationbias: {
                        point: `${location.lat},${location.lng}`,
                        radius: 10000,
                    },
                    key: this.$conf.public.googleapisKey,
                    language: this.$i18n.locale,
                    fields: ['name', 'formatted_address', 'geometry'],
                },
                headers: {
                    'Access-Control-Allow-Origin': '*',
                },
            })*/
            const service = new this.google.maps.places.PlacesService(this.map)
            service.textSearch(
                {
                    query: val,
                    radius: 30000,
                    location,
                },
                (results, status) => {
                    if (status === this.google.maps.places.PlacesServiceStatus.OK) {
                        this.items = results
                    }
                    this.loading = false
                }
            )
        }

        change() {
            if (this.location) {
                const center = this.location.geometry.location.toJSON()

                if (!this.hideRadius) {
                    this.setCircle(center)
                } else {
                    this.setCenter(center)
                }
            }
            this.$refs.input.blur()
        }

        setRadius() {
            this.circle.setRadius(Number(this.radius) * 1000)
        }

        setCircle(center) {
            this.radius = (Number(hotelsRuntimeStore.runtimeFilters.map.radius) / 1000).toFixed(2)
            if (this.circle) toRaw(this.circle).setMap(null)
            if (this.marker) toRaw(this.marker).setMap(null)
            this.circle = new this.google.maps.Circle({
                strokeColor: this.$vuetify.theme.themes.light.colors.primary,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: this.$vuetify.theme.themes.light.colors.primary,
                fillOpacity: 0.35,
                center,
                // radius: hotelsRuntimeStore.runtimeFilters.map.radius,
                radius: Number(this.radius) * 1000,
                editable: true,
            })
            this.circle.setMap(this.map)
            this.centerFilter(center)
            this.marker = new this.google.maps.Marker({
                position: center,
                icon: this.createMarkerIcon(),
                map: this.map,
            })
            this.circle.addListener('radius_changed', () => {
                const runtimeFilters = clone(hotelsRuntimeStore.runtimeFilters)
                runtimeFilters.map.radius = this.circle.radius
                this.radius = (this.circle.radius / 1000).toFixed(2)
                hotelsRuntimeStore.SET_RUNTIME_FILTERS(runtimeFilters)
                hotelsRuntimeStore.filter()
            })
            this.circle.addListener('center_changed', () => {
                this.location = null
                const center = this.circle.center.toJSON()
                this.centerFilter(center)
                this.marker.setPosition(center)
            })
        }

        setCenter(center) {
            if (this.marker) toRaw(this.marker).setMap(null)
            this.centerFilter(center)
            this.marker = new this.google.maps.Marker({
                position: center,
                icon: this.createMarkerIcon(),
                map: this.map,
            })
        }

        clear() {
            if (!this.hideRadius) {
                if (!this.circle) return
                toRaw(this.circle).setMap(null)
                this.circle = null
            }
            if (this.marker) toRaw(this.marker).setMap(null)
            this.marker = null
            this.centerFilter(null)
            this.location = null
        }

        markerColor() {
            return this.$vuetify.theme.themes.light.colors.success
        }

        centerFilter(center) {
            const runtimeFilters = clone(hotelsRuntimeStore.runtimeFilters)
            runtimeFilters.map.center = center
            hotelsStore.SET_FILTER({
                key: 'distance',
                value: this.location || !center ? [] : [distanceTransformer(Number(this.radius))],
            })
            hotelsRuntimeStore.SET_RUNTIME_FILTERS(runtimeFilters)
            hotelsRuntimeStore.filter()
        }

        get cityLocation() {
            return {lat: this.city.latitude, lng: this.city.longitude}
        }
    }

    export default HotelMapLocationFilter
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .location {
        width: 15vw;

        @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
            width: 60vw;
        }
    }

    .radius-field {
        width: 5vw;
    }
</style>
